import { computed, ref } from 'vue';
import { printAreas } from './compositionHelper';
import {
  MIN_PRINT_AREA_DESIGNS,
  toHotspotConfigs,
} from './multiPrintAreaCreation';

export type PickedDesign = { id: number; name?: string; href: string };

const PRINT_AREA_GROUPS = Object.keys(printAreas);

/**
 * The placement is the state: there is no separate list of picked designs. The main design is never
 * unset while something is placed, so `withPrintAreaDesigns` cannot be reached in a failing state.
 */
export const useMultiPrintAreaSelection = () => {
  const placement = ref<Record<string, PickedDesign | null>>(
    Object.fromEntries(PRINT_AREA_GROUPS.map((group) => [group, null])),
  );
  const mainDesignId = ref<number | null>(null);

  const placed = computed(() =>
    PRINT_AREA_GROUPS.map((group) => ({
      group,
      design: placement.value[group],
    })).filter(
      (entry): entry is { group: string; design: PickedDesign } =>
        !!entry.design,
    ),
  );

  const freeGroups = computed(() =>
    PRINT_AREA_GROUPS.filter((group) => !placement.value[group]),
  );

  const groupOfDesign = (designId: number) =>
    placed.value.find(({ design }) => design.id === designId)?.group;

  const isSelected = (designId: number) => !!groupOfDesign(designId);
  const isMainDesign = (designId: number) => mainDesignId.value === designId;

  const limitReached = computed(() => !freeGroups.value.length);
  const canSelect = (designId: number) =>
    isSelected(designId) || !limitReached.value;

  const isComplete = computed(
    () =>
      placed.value.length >= MIN_PRINT_AREA_DESIGNS &&
      mainDesignId.value !== null,
  );

  const setMainDesign = (designId: number) => {
    if (isSelected(designId)) mainDesignId.value = designId;
  };

  const removeFrom = (group: string) => {
    const design = placement.value[group];
    if (!design) return;

    placement.value[group] = null;
    if (isMainDesign(design.id)) {
      mainDesignId.value = placed.value[0]?.design.id ?? null;
    }
  };

  const toggle = (design: PickedDesign) => {
    const group = groupOfDesign(design.id);
    if (group) {
      removeFrom(group);
      return;
    }

    const free = freeGroups.value[0];
    if (!free) return;

    placement.value[free] = design;
    if (mainDesignId.value === null) mainDesignId.value = design.id;
  };

  /** Swaps two print areas, so a drop onto a taken one exchanges the designs. */
  const move = (fromGroup: string, toGroup: string) => {
    if (fromGroup === toGroup || !placement.value[fromGroup]) return;

    const moved = placement.value[fromGroup];
    placement.value[fromGroup] = placement.value[toGroup];
    placement.value[toGroup] = moved;
  };

  const designIds = computed(() => placed.value.map(({ design }) => design.id));

  const hotspotConfigs = computed(() =>
    toHotspotConfigs(
      Object.fromEntries(
        placed.value.map(({ group, design }) => [group, design.id]),
      ),
    ),
  );

  return {
    placement,
    placed,
    mainDesignId,
    designIds,
    hotspotConfigs,
    isSelected,
    isMainDesign,
    canSelect,
    limitReached,
    isComplete,
    toggle,
    move,
    removeFrom,
    setMainDesign,
  };
};
